import { branding, clinicTenantId } from './config';
import { isAuthenticated, login, logout } from './auth';
import { applyBranding, clinicName, loadClinicAssignment, loadClinicBranding, logoUrl } from './entitlements';

export async function renderLanding(root: HTMLElement): Promise<void> {
  if (clinicTenantId() === 'genesis') {
    await renderGenesisLanding(root);
    return;
  }
  if (clinicTenantId() !== branding.tenantId) {
    await renderSpecialtyLanding(root);
    return;
  }
  const line = await loadClinicBranding();
  applyBranding(line);
  const name = clinicName(line);
  const inSession = isAuthenticated();
  document.title = name;
  root.innerHTML = `
    <header class="nav flex items-center gap-4 bg-brand px-6 py-3 text-white">
      <a class="brand" href="/">
        <img src="${logoUrl(line.hasLogo)}" alt="" />
        ${escapeHtml(name)}
      </a>
      <nav>
        <a href="#servicios">Servicios</a>
        <a href="#consulta">Consulta</a>
        <a href="#nosotros">La clínica</a>
      </nav>
      <span class="spacer"></span>
      ${
        inSession
          ? `<a class="btn ghost" href="/app">Ir al consultorio</a><button class="btn ghost" type="button" id="salir">Salir</button>`
          : `<button class="btn ghost" type="button" id="entrar">Entrar</button>`
      }
    </header>
    <div class="hero-wrap">
      <section class="hero">
        <div>
          <p class="eyebrow">${escapeHtml(branding.city)} · Odontología</p>
          <h1 class="font-serif text-4xl font-semibold leading-tight text-ink">Cuidado dental con historia clínica clara y receta válida.</h1>
          <p class="lead">
            ${escapeHtml(line.tagline || branding.tagline)}.
            En ${escapeHtml(name)} el odontólogo dicta la consulta; el expediente
            y la receta salen con los datos que exige la normativa sanitaria del Ecuador.
          </p>
          <div class="cta">
            <a class="btn primary" href="/app" data-clinic>Abrir consultorio</a>
            <a class="btn ghost" href="#servicios">Ver servicios</a>
          </div>
        </div>
        <aside class="hero-card">
          <h2>En cada atención</h2>
          <ol>
            <li>Identificación del paciente y alergias.</li>
            <li>Motivo, examen y diagnóstico con CIE.</li>
            <li>Receta con nombre genérico (DCI) y registro ACESS.</li>
            <li>Copia de respaldo en la historia clínica.</li>
          </ol>
        </aside>
      </section>
    </div>
    <section id="servicios" class="section">
      <h2>Servicios</h2>
      <div class="grid">
        <article class="card">
          <p class="kicker">Consulta</p>
          <h3>Odontología general</h3>
          <p>Diagnóstico, plan de tratamiento y seguimiento con odontograma y CIE vigentes.</p>
        </article>
        <article class="card">
          <p class="kicker">Expediente</p>
          <h3>Historia clínica única</h3>
          <p>Registro sistemático alineado al formulario MSP 033 de odontología: filiación, examen, diagnóstico y prescripciones.</p>
        </article>
        <article class="card">
          <p class="kicker">Farmacia</p>
          <h3>Receta ACESS</h3>
          <p>Prescripción legible: DCI, forma farmacéutica, concentración, vía, cantidad en números y letras, dosis y vigencia.</p>
        </article>
      </div>
    </section>
    <section id="consulta" class="section">
      <h2>Cómo trabajamos</h2>
      <div class="trust">
        <div><strong>Confidencial</strong>La historia es única y reservada (Ley Orgánica de Salud, art. 7.f).</div>
        <div><strong>Trazable</strong>La receta concuerda con lo escrito en la historia (ACESS-2023-0030).</div>
        <div><strong>Quito</strong>${escapeHtml(branding.city)} · Atención ambulatoria de especialidad odontológica.</div>
      </div>
    </section>
    <section id="nosotros" class="section">
      <article class="about">
        <p class="kicker">Establecimiento</p>
        <h2>${escapeHtml(name)}</h2>
        <p>
          Consultorio odontológico. El profesional facultado registra la atención y emite
          documentos clínicos para el paciente: historia, receta e indicaciones de tratamiento.
        </p>
      </article>
    </section>
    <footer class="site-foot">
      <span>${escapeHtml(name)} · ${escapeHtml(branding.city)}</span>
      <span>Historia clínica y receta según MSP / ACESS</span>
    </footer>
  `;
  bindClinicActions(root);
}

const specialtyNames: Record<string, string> = {
  pediatria: 'Pediatría',
  ginecologia: 'Ginecología',
  endocrinologia: 'Endocrinología',
  'medicina-general': 'Medicina general',
  obstetricia: 'Obstetricia',
  'odontologia-general': 'Odontología general',
  ortodoncia: 'Ortodoncia',
  endodoncia: 'Endodoncia',
  psicologia: 'Psicología',
  'terapia-fisica': 'Terapia física',
};

const specialtyDescriptions: Record<string, string> = {
  pediatria: 'Atención integral para niñas, niños y adolescentes.',
  ginecologia: 'Cuidado preventivo y atención de la salud de la mujer.',
  endocrinologia: 'Evaluación y seguimiento de condiciones hormonales y metabólicas.',
  'medicina-general': 'Valoración inicial, prevención y seguimiento continuo.',
  obstetricia: 'Acompañamiento profesional durante el embarazo y posparto.',
  'odontologia-general': 'Prevención, diagnóstico y tratamiento de la salud bucal.',
  ortodoncia: 'Evaluación y corrección de la posición dental y maxilar.',
  endodoncia: 'Diagnóstico y tratamiento especializado de la pulpa dental.',
  psicologia: 'Acompañamiento para el bienestar emocional y la salud mental.',
  'terapia-fisica': 'Recuperación funcional, movilidad y manejo físico del dolor.',
};

async function renderGenesisLanding(root: HTMLElement): Promise<void> {
  const [line, receivedAssignment] = await Promise.all([loadClinicBranding(), loadClinicAssignment()]);
  const assignment = receivedAssignment?.tenantId === 'genesis' ? receivedAssignment : null;
  applyBranding(line);
  const name = clinicName(line);
  const specialties = assignment?.specialties || [];
  const hasClinic = assignment?.products?.vyedocs === true;
  document.title = `${name} · Centro de especialidades`;
  root.innerHTML = `
    <header class="nav genesis-nav">
      <a class="brand" href="/">
        <img src="${logoUrl(line.hasLogo)}" alt="" />
        <span><strong>${escapeHtml(name)}</strong><small>Centro de especialidades</small></span>
      </a>
      <nav><a href="#especialidades">Especialidades</a><a href="#atencion">Atención</a><a href="#documentos">Documentos</a></nav>
      <span class="spacer"></span>
      ${isAuthenticated()
        ? '<a class="btn ghost" href="/app">Consultorio</a><button class="btn ghost" type="button" id="salir">Salir</button>'
        : '<button class="btn ghost" type="button" id="entrar">Acceso profesional</button>'}
    </header>
    <main>
      <div class="genesis-hero">
        <section class="hero">
          <div>
            <p class="eyebrow">Salud integral · atención coordinada</p>
            <h1>${escapeHtml(line.heroTitle || 'Especialistas para cada etapa de tu bienestar')}</h1>
            <p class="lead">${escapeHtml(line.intro || 'Un centro de especialidades que reúne atención médica, odontológica, psicológica y de rehabilitación en un mismo lugar.')}</p>
            <div class="cta">
              <a class="btn primary" href="#especialidades">Conocer especialidades</a>
              <a class="btn quiet" href="/app" data-clinic>Ingresar al consultorio</a>
            </div>
            <p class="availability">${hasClinic
              ? 'Servicios configurados para este centro'
              : 'El acceso clínico requiere una cuenta autorizada y la activación del servicio para este centro.'}</p>
          </div>
          <aside class="genesis-symbol" aria-label="Atención multidisciplinaria">
            <span class="symbol-ring"></span><strong>G</strong>
            <p>Una historia clínica organizada por especialidad</p>
          </aside>
        </section>
      </div>
      <section id="especialidades" class="section genesis-section">
        <p class="eyebrow">Nuestro equipo</p><h2>Especialidades disponibles</h2>
        <p class="section-intro">Cada consulta conserva su contexto clínico y sus documentos propios.</p>
        <div class="genesis-grid">${specialties.map((specialty, index) => `<article class="specialty-card">
          <span>${String(index + 1).padStart(2, '0')}</span><h3>${escapeHtml(specialtyNames[specialty] || specialty.replace(/-/g, ' '))}</h3>
          <p>${escapeHtml(specialtyDescriptions[specialty] || 'Atención profesional de la especialidad.')}</p>
        </article>`).join('')}</div>
      </section>
      <section id="atencion" class="genesis-band"><div class="section">
        <p class="eyebrow">Tu recorrido</p><h2>Una atención clara de principio a fin</h2>
        <div class="care-steps"><article><b>1</b><h3>Admisión</h3><p>Confirmamos identidad, consentimiento y datos necesarios para la consulta.</p></article>
        <article><b>2</b><h3>Consulta</h3><p>El profesional registra antecedentes, examen, diagnóstico e indicaciones.</p></article>
        <article><b>3</b><h3>Seguimiento</h3><p>La historia y los documentos quedan organizados para la continuidad asistencial.</p></article></div>
      </div></section>
      <section id="documentos" class="section genesis-section">
        <div class="document-panel"><div><p class="eyebrow">Documentación clínica</p><h2>Información revisada por el profesional</h2>
        <p>El dictado ayuda a completar los campos. El diagnóstico, los códigos CIE y cualquier receta requieren confirmación clínica antes de guardarse.</p></div>
        <ul><li>Historia clínica por especialidad</li><li>Diagnóstico y códigos confirmados</li><li>Indicaciones y receta cuando corresponda</li></ul></div>
      </section>
    </main>
    <footer class="site-foot"><span>${escapeHtml(name)} · Centro de especialidades</span><span>Atención clínica responsable</span></footer>`;
  bindClinicActions(root);
}

async function renderSpecialtyLanding(root: HTMLElement): Promise<void> {
  const [line, receivedAssignment] = await Promise.all([loadClinicBranding(), loadClinicAssignment()]);
  const assignment = receivedAssignment?.tenantId === clinicTenantId() ? receivedAssignment : null;
  applyBranding(line);
  const name = clinicName(line);
  const hasClinic = assignment?.products?.vyedocs === true;
  const specialties = assignment?.specialties || [];
  document.title = name;
  root.innerHTML = `
    <header class="nav flex items-center gap-4 bg-brand px-6 py-3 text-white">
      <a class="brand" href="/">
        <img src="${logoUrl(line.hasLogo)}" alt="" />${escapeHtml(name)}
      </a>
      ${assignment ? '<nav><a href="#servicios">Especialidades</a><a href="#proceso">Cómo funciona</a></nav>' : ''}
      <span class="spacer"></span>
      ${hasClinic ? (isAuthenticated()
        ? '<a class="btn ghost" href="/app">Consultorio</a><button class="btn ghost" type="button" id="salir">Salir</button>'
        : '<button class="btn ghost" type="button" id="entrar">Entrar</button>') : ''}
    </header>
    <div class="hero-wrap">
      <section class="hero">
        <div>
          <p class="eyebrow">${escapeHtml(name)} · Centro de especialidades</p>
          <h1 class="font-serif text-4xl font-semibold leading-tight text-ink">${escapeHtml(assignment
            ? line.heroTitle || 'Atención organizada por especialidad'
            : 'Sitio en preparación')}</h1>
          <p class="lead">${escapeHtml(line.intro || line.tagline || (assignment
            ? 'Conoce las especialidades disponibles y accede a tu consultorio.'
            : 'Estamos preparando la información de este centro.'))}</p>
          ${hasClinic ? '<div class="cta"><a class="btn primary" href="/app" data-clinic>Ingresar al consultorio</a><a class="btn ghost" href="#servicios">Ver especialidades</a></div>' : ''}
        </div>
        ${assignment ? `<aside class="hero-card"><h2>Proceso de atención</h2><ol>
          <li>Admisión y confirmación de datos del paciente.</li>
          <li>Consulta con el profesional de la especialidad.</li>
          <li>Revisión de historia clínica, indicaciones y receta cuando corresponda.</li>
        </ol></aside>` : ''}
      </section>
    </div>
    ${assignment ? `<section id="servicios" class="section"><h2>Especialidades</h2>
      <div class="grid">${specialties.map((specialty) => `<article class="card"><h3>${escapeHtml(specialtyNames[specialty] || specialty.replace(/-/g, ' '))}</h3></article>`).join('')}</div>
    </section>
    <section id="proceso" class="section"><h2>Documentos de la consulta</h2>
      <div class="trust"><div><strong>Historia clínica</strong>El profesional confirma la información registrada.</div>
      <div><strong>Diagnóstico</strong>Las sugerencias CIE requieren revisión clínica.</div>
      <div><strong>Receta</strong>Se emite sólo cuando corresponde y tras verificar el tratamiento.</div></div>
    </section>` : ''}
    <footer class="site-foot"><span>${escapeHtml(name)}</span><span>${assignment ? 'Información y servicios del centro' : 'Próximamente'}</span></footer>
  `;
  bindClinicActions(root);
}

function bindClinicActions(root: HTMLElement): void {
  root.querySelector('#entrar')?.addEventListener('click', () => void login('/app'));
  root.querySelector('#salir')?.addEventListener('click', () => void logout());
  root.querySelectorAll<HTMLAnchorElement>('[data-clinic]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      if (isAuthenticated()) return;
      ev.preventDefault();
      void login('/app');
    });
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}
